import { LoadingButton } from "@mui/lab";
import { Card, CardContent, Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Stack, Typography, Grid, Tabs, Tab, MenuItem, Select, SelectChangeEvent, FormControl, Chip, Divider, LinearProgress } from "@mui/material";

import moment from "moment";
import { FileTypeIcon } from "../../common/components/FileTypeIcon";
import ProgramFileUploadComponent from "../programFileUpload/programFileUpload";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { IAbhyasaBharathiFile, IAbhyasaBharathiFileDeleteRequest, IAbhyasaBharathiProps } from "./abhyasaBharathiModels";
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AbhyasaBharathiService from "./abhyasaBharathiService";
import { AbhyasaBharathiFilters } from "../../common/data/abhyasaBharathi";
import { TabPanelProps } from "./abhyasaBharathiModels";
import React from "react";
import { AcademicsData, ActiveAcademicYear } from "../../common/data/academics";
import { getFileNameWithOutExt } from "../../common/utilities/utils";

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            style={{ width: '100%' }}
            {...other}
        >
            {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
        </div>
    );
}

export const AbhyasaBharathiMagazine = (props: IAbhyasaBharathiProps) => {
    const { isAddFilesVisible, isDeleteFilesVisible } = props;
    const [tabValue, setTabValue] = React.useState(0);
    const [abhyasaBharathiFiles, setAbhyasaBharathiFiles] = useState<IAbhyasaBharathiFile[]>([]);
    const [fileUploadIsInProgress, setFileUploadIsInProgress] = useState<boolean>(false);
    const [filesLoading, setFilesLoading] = useState<boolean>(false);
    const [academicYear, setAcademicYear] = useState<string>(ActiveAcademicYear);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const loadFiles = (year: string) => {
        setFilesLoading(true);
        AbhyasaBharathiService.getFiles(year).then((result) => {
            if (result.data.errors && result.data.errors.length > 0) {
                toast.error(result.data.errors[0].message);
            } else {
                setAbhyasaBharathiFiles(result.data.list);
            }
        }).finally(() => setFilesLoading(false));
    };

    useEffect(() => {
        loadFiles(academicYear);
    }, [academicYear]);

    const handleFileUpload = (filterId: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const uploadedFiles = Array.from(e.target.files || []);
        setFileUploadIsInProgress(true);
        AbhyasaBharathiService.fileUpload(filterId, academicYear, uploadedFiles)
            .then((response) => {
                if (response.data.errors?.length > 0) {
                    toast.error("Error uploading files");
                } else {
                    setAbhyasaBharathiFiles([...abhyasaBharathiFiles, ...response.data.list]);
                }
            }).finally(() => setFileUploadIsInProgress(false));
    };

    const handleDownloadClick = (programFile: IAbhyasaBharathiFile) => {
        window.open(AbhyasaBharathiService.getFileDownloadUrl(programFile.fileId), '_blank');
    };

    const handleFileDelete = (fileDetails: IAbhyasaBharathiFile) => {
        if (!window.confirm(`Delete "${getFileNameWithOutExt(fileDetails.fileName)}"?`)) return;
        const updated = [...abhyasaBharathiFiles];
        const idx = updated.findIndex(i => i.fileId === fileDetails.fileId);
        updated[idx] = { ...fileDetails, isDeleteInProgress: true };
        setAbhyasaBharathiFiles(updated);

        AbhyasaBharathiService.deleteFile({ fileId: fileDetails.fileId } as IAbhyasaBharathiFileDeleteRequest)
            .then((response) => {
                if (response.data.errors?.length > 0) {
                    toast.error("Error deleting file");
                } else {
                    setAbhyasaBharathiFiles(prev => prev.filter(i => i.fileId !== fileDetails.fileId));
                    toast.success("File deleted successfully");
                }
            });
    };

    const handleAcademicYearChange = (event: SelectChangeEvent<string>) => {
        setAcademicYear(event.target.value);
    };

    const activeFilter = AbhyasaBharathiFilters[tabValue];
    const filteredFiles = activeFilter
        ? abhyasaBharathiFiles.filter(i => i.filterId === activeFilter.filterId)
        : [];

    return (
        <Grid container sx={{ height: "100%" }}>
            <Grid item xs={12}>
                <Card sx={{ height: "100%" }}>
                    {/* Page header */}
                    <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                Abhyasa Bharathi Magazine
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                Magazine Files &amp; Archives
                            </Typography>
                        </Box>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <Select
                                value={academicYear}
                                onChange={handleAcademicYearChange}
                                displayEmpty
                            >
                                {AcademicsData.map((academic, index) => (
                                    <MenuItem key={index} value={academic}>{academic}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Chip
                            label={`${filteredFiles.length} file${filteredFiles.length !== 1 ? 's' : ''}`}
                            variant="outlined"
                            size="small"
                        />
                    </Box>
                    <Divider />
                    {filesLoading && <LinearProgress />}
                    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex', height: '100%' }}>
                            {/* Vertical filter tabs */}
                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                orientation="vertical"
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    borderRight: 1,
                                    borderColor: 'divider',
                                    minWidth: 160,
                                    bgcolor: 'grey.50',
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.82rem',
                                        alignItems: 'flex-start',
                                        pl: 2,
                                        minHeight: 44,
                                    },
                                    '& .Mui-selected': { fontWeight: 700 },
                                }}
                            >
                                {AbhyasaBharathiFilters.map((filter, index) => (
                                    <Tab key={index} label={filter.filterName} {...a11yProps(index)} />
                                ))}
                            </Tabs>

                            {/* Tab panels */}
                            {AbhyasaBharathiFilters.map((filter, index) => (
                                <CustomTabPanel key={index} value={tabValue} index={index}>
                                    {isAddFilesVisible && (
                                        <ProgramFileUploadComponent
                                            handleFileChange={(e) => handleFileUpload(filter.filterId, e)}
                                            fileUploadIsInProgress={fileUploadIsInProgress}
                                        />
                                    )}
                                    <TableContainer sx={{ width: '100%', maxHeight: '70vh', minHeight: '70vh', overflowY: 'auto' }}>
                                        <Table sx={{ minWidth: 500 }} aria-label="files table" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', bgcolor: 'grey.50' }}>File Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', bgcolor: 'grey.50' }}>Uploaded Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', bgcolor: 'grey.50' }}>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {abhyasaBharathiFiles.filter(i => i.filterId === filter.filterId).length === 0 && !filesLoading && (
                                                    <TableRow>
                                                        <TableCell colSpan={3} align="center">
                                                            <Typography color="text.secondary" sx={{ py: 4 }}>No files uploaded yet.</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {abhyasaBharathiFiles.filter(i => i.filterId === filter.filterId).map((programFile) => (
                                                    <TableRow key={programFile.fileId} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                                        <TableCell>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <FileTypeIcon fileName={programFile.fileName} />
                                                                <Typography sx={{ fontSize: '0.875rem' }}>{getFileNameWithOutExt(programFile.fileName)}</Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{moment(programFile.createdDate).format('DD-MMM-YYYY HH:mm')}</TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            <Stack direction="row" spacing={1}>
                                                                <LoadingButton
                                                                    variant="contained"
                                                                    size="small"
                                                                    startIcon={<CloudDownloadIcon />}
                                                                    onClick={() => handleDownloadClick(programFile)}
                                                                >
                                                                    Download
                                                                </LoadingButton>
                                                                {isDeleteFilesVisible && (
                                                                    <LoadingButton
                                                                        loading={programFile.isDeleteInProgress}
                                                                        variant="outlined"
                                                                        color="error"
                                                                        size="small"
                                                                        startIcon={<DeleteIcon />}
                                                                        loadingPosition="start"
                                                                        onClick={() => handleFileDelete(programFile)}
                                                                    >
                                                                        Delete
                                                                    </LoadingButton>
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CustomTabPanel>
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
}